import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  ExportOutlined,
  ImportOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Modal, Spin, Table, Tooltip, message } from "antd";
import GridSettings, { applyGridSettings, loadGridSettings } from "@controls/grid-settings";
import {
  copySprNumberPrice,
  deleteSprNumberPrice,
  exportSprNumberPriceXml,
  fetchSprNumberPriceList,
  importSprNumberPriceXml
} from "./spr-number-price-api";
import SprNumberPrice from "./spr-number-price";
import SprNumberPriceContent from "./spr-number-price-content";
import {
  formatDate,
  isPriceCurrent,
  normalizeZag,
  toNumber
} from "./spr-number-price-utils";
import "./spr-number-prices.css";

const GRID_STORAGE_KEY = "planOnline.sprNumberPrice.zagGrid.v2";

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const priceLabel = (row) =>
  row?.name || (row?.idNumberPrice != null ? `№${row.idNumberPrice}` : "");

/**
 * Прайс-листы номерного фонда. Delphi: вкладка «Прайс-листы» TFrmSprNumber.
 * Список шапок; содержимое (строки прайса) — в drawer.
 */
const SprNumberPrices = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const isNew = routeId === "new";
  const contentId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const zagCreateOpen = isNew;
  const contentOpen = contentId != null;

  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 4) === 1;

  const [zagRows, setZagRows] = useState([]);
  const [loadingZag, setLoadingZag] = useState(true);
  const [error, setError] = useState("");
  const [contentRefreshKey, setContentRefreshKey] = useState(0);
  const [gridSettings, setGridSettings] = useState(() => loadGridSettings(GRID_STORAGE_KEY));
  const [checkedIds, setCheckedIds] = useState([]);
  const fileInputRef = useRef(null);
  const zagRequestRef = useRef(0);

  useEffect(() => {
    try {
      localStorage.removeItem("planOnline.sprNumberPrice.selectedId");
    } catch {
      /* ignore */
    }
  }, []);

  const loadZag = useCallback(() => {
    const requestId = ++zagRequestRef.current;
    setLoadingZag(true);
    setError("");
    fetchSprNumberPriceList()
      .then((data) => {
        if (requestId !== zagRequestRef.current) return;
        const rows = (Array.isArray(data) ? data : [])
          .map(normalizeZag)
          .filter((r) => r.idNumberPrice != null);
        setZagRows(rows);
        setCheckedIds((prev) => prev.filter((id) => rows.some((r) => r.idNumberPrice === id)));
      })
      .catch((e) => {
        if (requestId !== zagRequestRef.current) return;
        setError(e.message || "Не удалось загрузить прайс-листы");
        setZagRows([]);
        setCheckedIds([]);
      })
      .finally(() => {
        if (requestId === zagRequestRef.current) setLoadingZag(false);
      });
  }, []);

  useEffect(() => {
    if (canEdit) loadZag();
  }, [canEdit, loadZag]);

  const checkedRows = useMemo(
    () => zagRows.filter((r) => checkedIds.includes(r.idNumberPrice)),
    [zagRows, checkedIds]
  );

  const hasChecked = checkedIds.length > 0;
  const hasOneChecked = checkedIds.length === 1;
  const importTargetId = hasOneChecked ? checkedIds[0] : null;

  const openCreate = useCallback(() => {
    navigate("/number/price/new");
  }, [navigate]);

  const openContent = useCallback(
    (record) => {
      if (record?.idNumberPrice == null) return;
      navigate(`/number/price/${record.idNumberPrice}`);
    },
    [navigate]
  );

  const closeDrawers = useCallback(() => {
    navigate("/number/price");
  }, [navigate]);

  const onZagCreated = useCallback(
    (saved) => {
      loadZag();
      const newId = toNumber(saved?.idNumberPrice ?? saved?.IdNumberPrice);
      if (newId != null) {
        navigate(`/number/price/${newId}`, { replace: true });
      } else {
        navigate("/number/price", { replace: true });
      }
    },
    [loadZag, navigate]
  );

  const onZagChanged = useCallback(() => {
    loadZag();
  }, [loadZag]);

  const onZagDelete = useCallback(() => {
    if (!canEdit || !checkedRows.length) return;
    const ids = checkedRows.map((r) => r.idNumberPrice);
    const many = ids.length > 1;
    Modal.confirm({
      title: many ? `Удалить прайс-листы (${ids.length})?` : "Удалить прайс-лист?",
      content: many ? checkedRows.map(priceLabel).filter(Boolean).join(", ") : priceLabel(checkedRows[0]),
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          for (const id of ids) {
            await deleteSprNumberPrice(id);
          }
          message.success("Удалено");
          if (ids.includes(contentId)) navigate("/number/price");
          setCheckedIds([]);
          loadZag();
        } catch (e) {
          message.error(e.message || "Не удалось удалить");
          loadZag();
          throw e;
        }
      }
    });
  }, [canEdit, checkedRows, contentId, loadZag, navigate]);

  const onZagCopy = useCallback(() => {
    if (!canEdit || !checkedRows.length) return;
    const ids = checkedRows.map((r) => r.idNumberPrice);
    const many = ids.length > 1;
    Modal.confirm({
      title: many ? `Копировать прайс-листы (${ids.length})?` : "Копировать прайс?",
      content: many ? checkedRows.map(priceLabel).filter(Boolean).join(", ") : priceLabel(checkedRows[0]),
      okText: "Копировать",
      cancelText: "Отмена",
      onOk: async () => {
        try {
          let lastId = null;
          for (const id of ids) {
            const created = await copySprNumberPrice(id);
            lastId = toNumber(created?.idNumberPrice ?? created?.IdNumberPrice) ?? lastId;
          }
          message.success("Скопировано");
          loadZag();
          if (lastId != null) navigate(`/number/price/${lastId}`);
        } catch (e) {
          message.error(e.message || "Не удалось скопировать");
          loadZag();
          throw e;
        }
      }
    });
  }, [canEdit, checkedRows, loadZag, navigate]);

  const onExport = useCallback(async () => {
    if (!checkedRows.length) return;
    try {
      for (const row of checkedRows) {
        const { blob, fileName } = await exportSprNumberPriceXml(row.idNumberPrice);
        downloadBlob(blob, fileName);
      }
      message.success("Экспортировано");
    } catch (e) {
      message.error(e.message || "Не удалось экспортировать");
    }
  }, [checkedRows]);

  const onImportClick = useCallback(() => {
    if (importTargetId == null) return;
    fileInputRef.current?.click();
  }, [importTargetId]);

  const onImportFile = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || importTargetId == null) return;
      const target = zagRows.find((r) => r.idNumberPrice === importTargetId);

      Modal.confirm({
        title: "Импорт прайса",
        content: `${priceLabel(target) || `№${importTargetId}`} будет замещен выбранным файлом. Продолжить?`,
        okText: "Импорт",
        cancelText: "Отмена",
        onOk: () =>
          file
            .text()
            .then((xml) => importSprNumberPriceXml(importTargetId, xml))
            .then(() => {
              message.success("Импортировано");
              if (contentId === importTargetId) {
                setContentRefreshKey((k) => k + 1);
              }
            })
            .catch((e) => {
              message.error(e.message || "Не удалось импортировать");
              return Promise.reject(e);
            })
      });
    },
    [importTargetId, zagRows, contentId]
  );

  const zagColumns = useMemo(
    () => [
      {
        title: "",
        label: "Статус",
        key: "status",
        width: 44,
        align: "center",
        render: (_, record) => {
          const current = isPriceCurrent(record);
          const label = current ? "Действует" : "Не действует";
          return (
            <Tooltip title={label}>
              <span
                className="spr-number-prices__status-dot"
                data-tone={current ? "on" : "off"}
                aria-label={label}
              />
            </Tooltip>
          );
        }
      },
      {
        title: "Начало",
        dataIndex: "dateBeg",
        key: "dateBeg",
        width: 110,
        render: formatDate
      },
      {
        title: "Окончание",
        dataIndex: "dateEnd",
        key: "dateEnd",
        width: 110,
        render: formatDate
      },
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        ellipsis: true
      },
      {
        title: "#",
        dataIndex: "idNumberPrice",
        key: "idNumberPrice",
        label: "Номер",
        width: 70
      }
    ],
    []
  );

  const columns = useMemo(
    () => applyGridSettings(zagColumns, gridSettings),
    [zagColumns, gridSettings]
  );

  const onGridSettingsChange = useCallback((next) => {
    setGridSettings(next);
  }, []);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: checkedIds,
      columnWidth: 36,
      onChange: (keys) => {
        setCheckedIds(keys.map(Number).filter(Number.isFinite));
      }
    }),
    [checkedIds]
  );

  if (!canEdit) {
    return (
      <div className="spr-number-prices">
        <Alert type="warning" showIcon message="Нет права «Настройка номеров»" />
      </div>
    );
  }

  return (
    <div className="spr-number-prices">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xml,text/xml,application/xml"
        style={{ display: "none" }}
        onChange={onImportFile}
      />
      {error ? (
        <Alert className="spr-number-prices__error" type="error" showIcon message={error} />
      ) : null}
      <div className="spr-number-prices__body">
        <div className="spr-number-prices__zag spr-number-prices__zag--full">
          <div className="spr-number-prices__toolbar">
            <div className="spr-number-prices__extra-actions">
              <Tooltip title="Создать прайс-лист">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  aria-label="Создать прайс-лист"
                  onClick={openCreate}
                />
              </Tooltip>
              <Tooltip title={hasChecked ? "Удалить" : "Отметьте прайс-листы"}>
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!hasChecked}
                    aria-label="Удалить"
                    onClick={onZagDelete}
                  />
                </span>
              </Tooltip>
              <Tooltip title={hasChecked ? "Копировать" : "Отметьте прайс-листы"}>
                <span>
                  <Button
                    icon={<CopyOutlined />}
                    disabled={!hasChecked}
                    aria-label="Копировать"
                    onClick={onZagCopy}
                  />
                </span>
              </Tooltip>
              <Tooltip title={hasChecked ? "Экспорт" : "Отметьте прайс-листы"}>
                <span>
                  <Button
                    icon={<ExportOutlined />}
                    disabled={!hasChecked}
                    aria-label="Экспорт"
                    onClick={onExport}
                  />
                </span>
              </Tooltip>
              <Tooltip title={hasOneChecked ? "Импорт" : "Отметьте один прайс-лист"}>
                <span>
                  <Button
                    icon={<ImportOutlined />}
                    disabled={!hasOneChecked}
                    aria-label="Импорт"
                    onClick={onImportClick}
                  />
                </span>
              </Tooltip>
              <GridSettings
                title="Настройка таблицы"
                storageKey={GRID_STORAGE_KEY}
                columns={zagColumns}
                value={gridSettings}
                onChange={onGridSettingsChange}
              />
            </div>
          </div>
          <div className="spr-number-prices__list-body">
            {loadingZag && !zagRows.length ? (
              <div className="spr-number-prices__center">
                <Spin tip="Загрузка..." />
              </div>
            ) : (
              <Table
                className="spr-number-prices__table"
                size="small"
                rowKey="idNumberPrice"
                columns={columns}
                dataSource={zagRows}
                loading={loadingZag}
                pagination={false}
                rowSelection={rowSelection}
                onRow={(record) => ({
                  onClick: (e) => {
                    if (e.target.closest(".ant-checkbox-wrapper, .ant-table-selection-column")) {
                      return;
                    }
                    openContent(record);
                  }
                })}
                rowClassName={(record) =>
                  [
                    contentId != null && record.idNumberPrice === contentId
                      ? "spr-number-prices__row--editing"
                      : "",
                    isPriceCurrent(record) ? "" : "spr-number-prices__row--stale"
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
                scroll={{ x: 520 }}
                locale={{ emptyText: "Нет прайс-листов" }}
              />
            )}
          </div>
        </div>
      </div>

      <SprNumberPrice
        open={zagCreateOpen}
        idNumberPrice={null}
        isNew
        onClose={closeDrawers}
        onSaved={onZagCreated}
      />
      <SprNumberPriceContent
        key={`${contentId ?? "none"}-${contentRefreshKey}`}
        open={contentOpen}
        idNumberPrice={contentId}
        onClose={closeDrawers}
        onZagChanged={onZagChanged}
      />
    </div>
  );
};

export default SprNumberPrices;
